import React, { useCallback, useEffect, useMemo } from "react";
import { useLocation, useNavigate, useOutletContext } from "react-router-dom";
import {
  Box,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { useDispatch } from "react-redux";
import { sortBy } from "underscore";

import {
  fetchOrdersForEvent,
  fetchSessionsForEvent,
  fetchTicketsForEvent,
} from "../../../redux/features/adminEventsSlice";
import OrdersList from "../../OrdersList";
import { buildValueText } from "../../OrdersList/OrdersList";

const toCSV = (table) => {
  return table
    .map((row) =>
      row
        .map((cell) => {
          if (
            cell
              ?.toString()
              .replace(/ /g, "")
              .match(/[\s,"]/)
          ) {
            return '"' + (cell || "").replace(/"/g, '""') + '"';
          }
          return cell || "";
        })
        .join(","),
    )
    .join("\n");
};

const priceText = (usdCents) => {
  let USDollar = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  });
  return USDollar.format(usdCents / 100);
};

const EventOrders = () => {
  const { event } = useOutletContext();
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const {
    id: eventId,
    organizationId,
    orders,
    tickets,
    sessions,
  } = event || {};

  useEffect(() => {
    if (eventId) {
      dispatch(fetchOrdersForEvent({ eventId, organizationId }));
      dispatch(fetchTicketsForEvent({ eventId }));
      dispatch(fetchSessionsForEvent({ eventId }));
    }
  }, [dispatch, eventId, organizationId]);

  const filterParams = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return {
      affiliate: (searchParams.get("affiliate") || "").trim(),
      promoCode: (searchParams.get("promoCode") || "").trim(),
    };
  }, [location.search]);

  const setFilterParam = useCallback(
    (key, value) => {
      const searchParams = new URLSearchParams(location.search);
      const trimmed = (value || "").trim();
      if (!trimmed) {
        searchParams.delete(key);
      } else {
        searchParams.set(key, trimmed);
      }
      const queryString = searchParams.toString();
      navigate(`${location.pathname}${queryString ? `?${queryString}` : ""}`, {
        replace: true,
      });
    },
    [location.pathname, location.search, navigate],
  );

  const { orderAffiliateById, orderPromoCodeById } = useMemo(() => {
    const affiliateById = {};
    const promoCodeById = {};
    const ticketsList = tickets || [];

    ticketsList.forEach((ticket) => {
      const orderId = ticket?.order?.id;
      if (!orderId) return;

      const affiliateValue =
        typeof ticket.affiliate === "string"
          ? ticket.affiliate
          : typeof ticket.affiliateName === "string"
          ? ticket.affiliateName
          : "";
      if (!affiliateById[orderId] && affiliateValue) {
        affiliateById[orderId] = affiliateValue;
      }

      const promoCodeValue =
        typeof ticket.promoCode === "string"
          ? ticket.promoCode
          : typeof ticket.appliedPromoCode === "string"
          ? ticket.appliedPromoCode
          : "";
      if (!promoCodeById[orderId] && promoCodeValue) {
        promoCodeById[orderId] = promoCodeValue;
      }
    });

    return { orderAffiliateById: affiliateById, orderPromoCodeById: promoCodeById };
  }, [tickets]);

  const getAffiliateValue = useCallback(
    (order) => {
      if (!order) return "";
      if (typeof order.affiliate === "string") return order.affiliate;
      if (typeof order.affiliateName === "string") return order.affiliateName;
      const derived = orderAffiliateById?.[order.id];
      return typeof derived === "string" ? derived : "";
    },
    [orderAffiliateById],
  );

  const getPromoCodeValue = useCallback(
    (order) => {
      if (!order) return "";
      if (typeof order.promoCode === "string") return order.promoCode;
      if (typeof order.appliedPromoCode === "string") return order.appliedPromoCode;
      const derived = orderPromoCodeById?.[order.id];
      return typeof derived === "string" ? derived : "";
    },
    [orderPromoCodeById],
  );

  const affiliateOptions = useMemo(() => {
    const values = (orders || []).map(getAffiliateValue).filter(Boolean);
    const selected = filterParams.affiliate;
    if (selected) {
      values.push(selected);
    }
    return [...new Set(values)].sort((a, b) => a.localeCompare(b));
  }, [orders, getAffiliateValue, filterParams.affiliate]);

  const promoCodeOptions = useMemo(() => {
    const values = (orders || []).map(getPromoCodeValue).filter(Boolean);
    const selected = filterParams.promoCode;
    if (selected) {
      values.push(selected);
    }
    return [...new Set(values)].sort((a, b) => a.localeCompare(b));
  }, [orders, getPromoCodeValue, filterParams.promoCode]);

  const ordersToDisplay = useMemo(() => {
    const affiliateFilter = filterParams.affiliate;
    const promoCodeFilter = filterParams.promoCode;
    const ordersList = orders || [];

    const filtered = ordersList.filter((order) => {
      if (affiliateFilter && getAffiliateValue(order) !== affiliateFilter) {
        return false;
      }
      if (promoCodeFilter && getPromoCodeValue(order) !== promoCodeFilter) {
        return false;
      }
      return true;
    });

    return filtered.map((order) => ({
      ...order,
      affiliate: getAffiliateValue(order),
      promoCode: getPromoCodeValue(order),
    }));
  }, [orders, filterParams, getAffiliateValue, getPromoCodeValue]);

  if (!event) {
    return null;
  }

  if (!orders) {
    return null;
  }

  if (!tickets) {
    return null;
  }

  if (!sessions) {
    return null;
  }

  return (
    <Box
      marginTop={"20px"}
      marginBottom={{ xs: "90px", md: "0px" }}
      position={"relative"}
    >
      <Box display={"flex"}>
        <Typography variant="h2" paddingTop={"20px"} sx={{ flexGrow: 1 }}>
          Orders
        </Typography>
        <Button
          startIcon={<DownloadIcon />}
          onClick={() => {
            const csvRows = [];
            const orderFormKeys = [];
            const orderFormNameMap = {};
            const orderFormTypeMap = {};

            const sessionCheckInFields = sessions.map(
              (session) => `Check-in Time: ${session.name}`,
            );

            const defaultFields = [
              "Order #",
              "Order Date",
              "Payment Method",
              "Ticket Name",
              "Ticket Price",
              "Promo Code",
              "Discount Amount",
              "Total Fees",
              "Amount Charged",
              "Affiliate",
              "Check-in Time",
              ...sessionCheckInFields,
            ];

            const ticketByOrderIdMap = tickets.reduce((acc, ticket) => {
              const { order } = ticket;
              const existing = acc[order.id] || [];
              return { ...acc, [order.id]: [...existing, ticket] };
            }, {});

            const sortedOrders = sortBy(orders, (order) => {
              return order.createdAt.toDate().getTime();
            });

            const pricedTickets = [];

            sortedOrders.forEach((order) => {
              const { id: orderId, items } = order;
              const tickets = ticketByOrderIdMap[orderId];

              const ticketByTypeKey = tickets.reduce((acc, ticket) => {
                const { ticketTypeKey } = ticket;
                const existing = acc[ticketTypeKey] || [];
                return { ...acc, [ticketTypeKey]: [...existing, ticket] };
              }, {});

              items.forEach(({ quantity, usdCentsEach, ticketTypeKey }) => {
                ticketByTypeKey[ticketTypeKey]
                  .slice(0, quantity)
                  .forEach((ticket) => {
                    pricedTickets.push({
                      ...ticket,
                      usdCents: usdCentsEach,
                      orderRef: order,
                    });
                  });
                ticketByTypeKey[ticketTypeKey] = ticketByTypeKey[
                  ticketTypeKey
                ].slice(1 + quantity);
              });
            });

            pricedTickets.forEach((ticket) => {
              const { purchaserOrderFormFieldsOriginal } = ticket;
              purchaserOrderFormFieldsOriginal.forEach(
                ({ key, type, name }) => {
                  const purchaserKey = `p-${key}`;
                  if (!orderFormKeys.includes(purchaserKey)) {
                    orderFormKeys.push(purchaserKey);
                    orderFormNameMap[purchaserKey] = `Purchaser ${name}`;
                    orderFormTypeMap[purchaserKey] = type;
                  }
                },
              );
            });

            pricedTickets.forEach((ticket) => {
              const { orderFormFieldsOriginal } = ticket;
              orderFormFieldsOriginal.forEach(({ key, type, name }) => {
                const attendeeKey = `a-${key}`;
                if (!orderFormKeys.includes(attendeeKey)) {
                  orderFormKeys.push(attendeeKey);
                  orderFormNameMap[attendeeKey] = name;
                  orderFormTypeMap[attendeeKey] = type;
                }
              });
            });

            const allFeeTypes = {};
            pricedTickets.forEach((ticket) => {
              (ticket.fees || []).forEach((fee) => {
                const feeKey = fee.name;
                if (!allFeeTypes[feeKey]) {
                  allFeeTypes[feeKey] = {
                    name: fee.name,
                    key: feeKey,
                  };
                }
              });
            });
            const feeKeys = Object.keys(allFeeTypes);
            const feeHeaders = feeKeys.map((key) => key);

            csvRows.push([
              ...defaultFields,
              ...orderFormKeys.map((key) => orderFormNameMap[key]),
              ...feeHeaders,
            ]);
            pricedTickets.forEach(
              ({
                purchaserOrderFormFieldsOriginal,
                orderFormFieldsOriginal,
                orderRef: order,
                usdCents,
                createdAt,
                name,
                appliedPromoCode,
                promoCode,
                discountUsdCents,
                checkIn,
                sessionCheckIns,
                affiliate,
                fees = [],
              }) => {
                const { id: orderId, paymentDetails } = order;
                const { captureDetails } = paymentDetails || {};
                const { card } = captureDetails || {};
                const { card_brand } = card || {};

                const defaultFieldsValues = [
                  orderId,
                  createdAt.toDate().toLocaleString(),
                  card_brand,
                ];

                const { createdAt: checkedInAt } = checkIn || {};

                const chargedUsdCents = Boolean(appliedPromoCode)
                  ? usdCents - discountUsdCents
                  : usdCents;

                const totalFeesUsdCents = (fees || []).reduce(
                  (sum, fee) => sum + fee.amount,
                  0,
                );

                const finalAmountCharged = chargedUsdCents + totalFeesUsdCents;

                const sessionCheckInTimes = sessions.map((session) => {
                  const sessionCheckIn = (sessionCheckIns || {})[session.id];
                  const { createdAt } = sessionCheckIn || {};
                  return createdAt?.toDate().toLocaleString();
                });

                const csvValues = [
                  ...defaultFieldsValues,
                  name,
                  priceText(usdCents),
                  Boolean(appliedPromoCode) ? promoCode : "",
                  Boolean(appliedPromoCode)
                    ? priceText(discountUsdCents)
                    : "$0.00",
                  priceText(totalFeesUsdCents),
                  priceText(finalAmountCharged),
                  affiliate || "",
                  checkedInAt?.toDate().toLocaleString(),
                  ...sessionCheckInTimes,
                ];

                orderFormKeys.forEach((orderFormKey) => {
                  const orderFormField = orderFormFieldsOriginal.find(
                    ({ key }) => `a-${key}` === orderFormKey,
                  );
                  const purchaserOrderFormField =
                    purchaserOrderFormFieldsOriginal.find(
                      ({ key }) => `p-${key}` === orderFormKey,
                    );

                  const value = (
                    orderFormField ||
                    purchaserOrderFormField ||
                    {}
                  ).value;
                  const type = orderFormTypeMap[orderFormKey];
                  const csvValue = buildValueText(value, type);
                  csvValues.push(csvValue);
                });

                feeKeys.forEach((feeKey) => {
                  const fee = (fees || []).find((f) => f.name === feeKey);
                  csvValues.push(fee ? priceText(fee.amount) : "$0.00");
                });

                csvRows.push(csvValues);
              },
            );

            const csv = toCSV(csvRows);
            var blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
            var url = URL.createObjectURL(blob);
            var pom = document.createElement("a");
            pom.href = url;
            pom.setAttribute("download", `event-orders-${eventId}.csv`);
            pom.click();
          }}
        >
          Download CSV
        </Button>
      </Box>

      <Box
        marginTop={2}
        marginBottom={2}
        display="flex"
        gap={2}
        flexWrap="wrap"
        alignItems="center"
      >
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id="event-orders-filter-affiliate-label">
            Affiliate
          </InputLabel>
          <Select
            labelId="event-orders-filter-affiliate-label"
            label="Affiliate"
            value={filterParams.affiliate}
            data-testid="event-orders-filter-affiliate"
            onChange={(e) => setFilterParam("affiliate", e.target.value)}
          >
            <MenuItem value="">All affiliates</MenuItem>
            {affiliateOptions.map((value) => (
              <MenuItem key={`affiliate-${value}`} value={value}>
                {value}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id="event-orders-filter-promoCode-label">
            Promo code
          </InputLabel>
          <Select
            labelId="event-orders-filter-promoCode-label"
            label="Promo code"
            value={filterParams.promoCode}
            data-testid="event-orders-filter-promoCode"
            onChange={(e) => setFilterParam("promoCode", e.target.value)}
          >
            <MenuItem value="">All promo codes</MenuItem>
            {promoCodeOptions.map((value) => (
              <MenuItem key={`promoCode-${value}`} value={value}>
                {value}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box display={"flex"} flexDirection={"column"} gap="20px">
        <OrdersList
          orders={ordersToDisplay}
          eventId={eventId}
          tickets={tickets}
          getOrderDetailsUrl={(order) => order.id}
          organizationId={organizationId}
        />
      </Box>
    </Box>
  );
};

export default EventOrders;
